/* eslint-disable no-console */
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Alert, Button, Form, Modal, message, Tabs } from 'antd';
import uuidv4 from 'uuid/v4';
import { withTranslation } from 'react-i18next';
import { getCellByLocation } from 'geohex';

import SignWindow from '../../../components/key/sign';
import { lookupRepo } from '../../../repository/lookup';
import money from '../../../helpers/money';
import { TransactionRepo } from '../../../repository/transaction';
import AccountTypeRepo from '../../../repository/account-type';
import InternalTransaction from './InternalTransaction';
import ExternalTransaction from './ExternalTransaction';
import { cleanRut } from '../../../helpers/transaction-utils';
import { SpeiExternalTransaction } from './SpeiExternalTransaction';
import { buildMxSpeiUri } from '../../batch/components/creation-helpers';

const { TabPane } = Tabs;

const baseExternalDestinationUri = 'cl-bank';

const NewTransaction = ({ business, account, form, t }) => {
  const { getFieldDecorator } = form;
  const [visible, setVisible] = useState(false);
  const [destination, setDestination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [signVisible, setSignVisible] = useState(false);
  const [payload, setPayload] = useState(null);
  const [now, setNow] = useState(null);
  const [id, setId] = useState(null);
  const [error, setError] = useState(null);
  const [location, setLocation] = useState(null);

  const actions = AccountTypeRepo.transactionActions(account.product_id);
  const [transactionType, setTransactionType] = useState(actions[0]);

  const setLocationFromBrowser = () => {
    if (!('geolocation' in navigator)) {
      return;
    }

    navigator.geolocation.getCurrentPosition((position) => {
      const hex = getCellByLocation(position.coords.latitude, position.coords.longitude, 7);
      setLocation(hex.code);
    });
  };

  const open = () => {
    const todaysDate = new Date();
    setNow(Math.floor(todaysDate / 1000));
    const newUuidv4 = uuidv4();
    const fullYear = todaysDate.getFullYear();
    const month = (`0${todaysDate.getMonth() + 1}`).slice(-2);
    const newId = `${fullYear}-${month}-${newUuidv4}`;
    setId(newId);
    setVisible(true);
    setLocationFromBrowser();
  };

  const close = () => {
    form.resetFields();
    setPayload(null);
    setDestination(null);
    setLoading(false);
    setVisible(false);
    setSignVisible(false);
    setLocation(null);
  };

  const destinationUrl = (value) => {
    if (!value || value.startsWith('http')) { return value; }
    return `${account.region_id.toLowerCase()}:${value}`;
  };

  const lookupURL = async (rule, value, callback) => {
    setDestination(null);
    try {
      const url = destinationUrl(value);
      const { data } = await lookupRepo.url(url);
      setDestination(data);
      callback();
    } catch (lookupError) {
      console.error('ERROR: Look up URL (new.jsx Component): ', lookupError);
      callback(t('transaction.url_not_found'));
    }
  };

  const formatRutNumber = (rutDigitoVerificador) => {
    const cleanedRut = cleanRut(rutDigitoVerificador);
    return cleanedRut;
  };

  const getExternalTransactionDestinationUri = ({ ext_rut: rut, ext_name: name, ext_account_type: accountType, ext_bank_code: bankCode, ext_account_number: accountNumber }) => {
    const parsedRut = formatRutNumber(rut);
    const uri = `${baseExternalDestinationUri}:${accountNumber}?name=${name}&bkid=${bankCode}&dstdocnum=${parsedRut}&bktype=${accountType}`;
    return uri;
  };

  const getSpeiExternalTransactionDestinationUri = ({ ext_name: name, ext_bank_account: clabe, ext_rfc_or_curp: rfcOrCurp }) => buildMxSpeiUri({ name, clabe, rfcOrCurp });

  const create = () => {
    form.validateFields({ suppressWarning: true }, (validateError, values) => {
      if (validateError) {
        return;
      }
      setLoading(true);
      switch (transactionType) {
        case 'internal':
          setPayload({
            id,
            vTS: now,
            bID: business.id,
            a: money.toInt(values.amount, account.currency, false),
            c: account.currency,
            s: account.gateway_uri,
            d: destination.gateway_uri,
            loc: location,
          });
          break;
        case 'external':
          setPayload({
            id,
            vTS: now,
            bID: business.id,
            a: money.toInt(values.ext_amount, account.currency, false),
            c: account.currency,
            s: account.gateway_uri,
            d: getExternalTransactionDestinationUri(values),
            loc: location,
          });
          break;
        case 'spei':
          setPayload({
            id,
            vTS: now,
            bID: business.id,
            a: money.toInt(values.ext_amount, account.currency, false),
            c: account.currency,
            s: account.gateway_uri,
            d: getSpeiExternalTransactionDestinationUri(values),
            loc: location,
          });
          break;
        default: break;
      }
      setSignVisible(true);
    });
  };

  const executeInternalTransaction = async (signature) => {
    setSignVisible(false);
    const values = form.getFieldsValue();
    const body = {
      id,
      currency: account.currency,
      destination_uri: destination.gateway_uri,
      value_ts: now,
      signature,
      loc: location,
      ...values,
    };
    try {
      await TransactionRepo.createNew(business, account, body);
      message.success(t('success.creating_transaction'));
      close();
    } catch (createError) {
      setError((createError.response) ? createError.response.data.message : createError.message);
      setLoading(false);
      message.error(t('error.creating_transaction'));
      console.error('ERROR: Internal Transaction (new.jsx Component): executeInternalTransaction()', createError); // eslint-disable-line no-console
    }
  };

  const executeExternalTransaction = async (signature) => {
    const values = form.getFieldsValue();
    const body = {
      id,
      amount: values.ext_amount,
      currency: account.currency,
      destination_uri: getExternalTransactionDestinationUri(values),
      value_ts: now,
      signature,
      loc: location,
      ...values,
    };
    try {
      const res = await TransactionRepo.createNew(business, account, body);
      if (res) {
        if (res.data.source.second_factor_auth) {
          // eslint-disable-next-line
          const code = prompt('Enter the code sent by sms');
          if (!code) {
            message.error(t('error.creating_transaction'));
          }

          await TransactionRepo.confirm2fa(business, account, {
            txid: res.data.id,
            code,
          });
        }
      }

      message.success(t('success.creating_external_transaction'));
      close();
    } catch (transactionCreateError) {
      setError((transactionCreateError.response) ? transactionCreateError.response.data.message : transactionCreateError.message);
      setLoading(false);
      message.error(t('error.creating_transaction'));
      console.error('ERROR: External Transaction (new.jsx Component): executeExternalTransaction()', transactionCreateError); // eslint-disable-line no-console
      close();
    }
  };

  const executeSpeiExternalTransaction = async (signature) => {
    const values = form.getFieldsValue();
    const body = {
      id,
      amount: values.ext_amount,
      currency: account.currency,
      destination_uri: getSpeiExternalTransactionDestinationUri(values),
      value_ts: now,
      signature,
      loc: location,
      desc: values.ext_desc,
      rfc_or_curp: values.ext_rfc_or_curp,
      ...values,
    };

    try {
      await TransactionRepo.createNew(business, account, body);
      message.success(t('success.creating_external_transaction'));
      close();
    } catch (transactionCreateError) {
      setError((transactionCreateError.response) ? transactionCreateError.response.data.message : transactionCreateError.message);
      setLoading(false);
      message.error(t('error.creating_transaction'));
      console.error('ERROR: External Transaction (new.jsx Component): executeSPEIExternalTransaction()', transactionCreateError); // eslint-disable-line no-console
      close();
    }
  };

  const executeTransaction = (signature) => {
    setSignVisible(false);
    switch (transactionType) {
      case 'internal':
        executeInternalTransaction(signature);
        break;
      case 'spei':
        executeSpeiExternalTransaction(signature);
        break;
      case 'external':
        executeExternalTransaction(signature);
        break;
      default:
        break;
    }
  };

  const handleTabChange = (tab) => {
    setDestination(null);
    setTransactionType(tab);
  };

  const renderTransactionForm = (type) => {
    if ((transactionType !== type) || (!visible)) {
      return;
    }
    switch (type) {
      case 'external':
        return <ExternalTransaction getFieldDecorator={getFieldDecorator} currency={account.currency} balance={account.balance.amount} />;
      case 'spei':
        return <SpeiExternalTransaction getFieldDecorator={getFieldDecorator} currency={account.currency} balance={account.balance.amount} />;
      default:
        return <InternalTransaction getFieldDecorator={getFieldDecorator} lookupURL={lookupURL} destination={destination} currency={account.currency} balance={account.balance.amount} />;
    }
  };

  const buildTabs = () => {
    if (actions.length === 1) { return renderTransactionForm(actions[0]); }
    return (
      <Tabs defaultActiveKey="1" onChange={handleTabChange}>
        {actions.map((action) => (
          <TabPane tab={t(`transaction.tab.${action}`)} key={action}>
            <br />
            {renderTransactionForm(action)}
          </TabPane>
        ))}
      </Tabs>
    );
  };

  return (
    <>
      <Modal
        visible={visible}
        title={t('create_transaction')}
        okText={t('create')}
        onCancel={close}
        onOk={create}
        footer={[
          <Button data-testid="close-transaction-button" key="back" onClick={close}>
            {t('cancel')}
          </Button>,
          <Button data-testid="accept-transaction-button" key="submit" type="primary" loading={loading} onClick={create}>
            {t('create')}
          </Button>,
        ]}
      >
        {(error === null) ? ('') : (<Alert message={t('error.creating_transaction')} description={error} type="error" showIcon banner style={{ margin: '-20px -24px 10px' }} />)}
        {buildTabs()}
      </Modal>
      <SignWindow
        visible={signVisible}
        payload={payload}
        onOk={(sig) => executeTransaction(sig.signature)}
        onCancel={() => {
          setSignVisible(false);
          setLoading(false);
        }}
      />
      <Button disabled={account.balance.amount === 0} onClick={open} icon="plus" type="primary">
        {t('create_transaction')}
      </Button>
    </>
  );
};

NewTransaction.propTypes = {
  account: PropTypes.shape({
    balance: PropTypes.shape({
      amount: PropTypes.number,
    }),
    currency: PropTypes.string,
    gateway_uri: PropTypes.string,
    product_id: PropTypes.string,
    region_id: PropTypes.string,
  }),
  business: PropTypes.shape({
    id: PropTypes.string,
  }),
  form: PropTypes.shape({
    getFieldsValue: PropTypes.func,
    resetFields: PropTypes.func,
    validateFields: PropTypes.func,
    getFieldDecorator: PropTypes.func,
  }),
  t: PropTypes.func,
};

export default Form.create({ name: 'new-transaction-window' })(withTranslation()(NewTransaction));
